import * as Sentry from '@sentry/react';

const PII_KEYS = new Set(['email', 'name', 'username', 'useremail', 'user_name']);

type AnyRecord = Record<string, unknown>;

const redactKeys = (value: unknown): AnyRecord | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const source = value as AnyRecord;
  const redacted: AnyRecord = { ...source };

  for (const key of Object.keys(redacted)) {
    const normalized = key.toLowerCase();
    if (PII_KEYS.has(normalized)) {
      delete redacted[key];
    }
  }

  return redacted;
};

const scrubEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
  if (event.user) {
    const redactedUser = { ...event.user };
    delete redactedUser.email;
    delete redactedUser.name;
    delete redactedUser.username;
    event.user = redactedUser;
  }

  if (event.extra) {
    const redactedExtra = redactKeys(event.extra);
    if (redactedExtra) {
      event.extra = redactedExtra;
    }
  }

  if (event.breadcrumbs?.length) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (!breadcrumb?.data) {
        return breadcrumb;
      }

      const redactedData = redactKeys(breadcrumb.data);
      if (!redactedData) {
        return breadcrumb;
      }

      return {
        ...breadcrumb,
        data: redactedData,
      };
    });
  }

  return event;
};

/**
 * Check if Sentry is configured (DSN is set).
 * Used by error boundaries to show "auto-reported" status.
 */
export function isSentryConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SENTRY_DSN);
}

export const initSentry = () => {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    tracesSampleRate: 0,
    beforeSend: scrubEvent,
  });
};
